import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, Save, Plus, Trash2, ExternalLink, Upload, X } from "lucide-react";
import { toast } from "sonner";

function TextRow({ label, value, onChange, area }) {
  return (
    <div>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {area
        ? <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} className="bg-slate-900/60 border-slate-700 mt-1" />
        : <Input value={value || ""} onChange={(e) => onChange(e.target.value)} className="bg-slate-900/60 border-slate-700 mt-1" />}
    </div>
  );
}

// Compress + resize an image file to a small JPEG data URL (deploy-safe, no server storage).
function compressImage(file, maxW = 512, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxW / img.width);
        const w = Math.round(img.width * scale), h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function ImageRow({ label, value, onChange, hint }) {
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); e.target.value = ""; return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("Image too large (max 8MB)"); e.target.value = ""; return; }
    setBusy(true);
    try {
      const dataUrl = await compressImage(file);
      onChange(dataUrl);
      toast.success("Image attached — click Save Founder to publish");
    } catch { toast.error("Could not read image"); }
    setBusy(false);
    e.target.value = "";
  };

  return (
    <div>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div className="mt-1.5 flex items-center gap-4">
        <div className="h-20 w-20 shrink-0 rounded-xl border border-slate-700 bg-slate-900/60 overflow-hidden flex items-center justify-center">
          {value ? <img src={value} alt="Founder" className="h-full w-full object-cover" data-testid="founder-photo-preview" />
                 : <Upload className="h-5 w-5 text-slate-600" />}
        </div>
        <div className="flex-1 space-y-2">
          <div className="flex items-center gap-2">
            <input ref={inputRef} type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid="founder-photo-input" />
            <Button type="button" variant="outline" disabled={busy} onClick={() => inputRef.current?.click()} data-testid="founder-photo-attach" className="border-slate-700">
              {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}{value ? "Replace Image" : "Attach Image"}
            </Button>
            {value && <Button type="button" variant="ghost" size="sm" onClick={() => onChange("")} data-testid="founder-photo-remove" className="text-red-400 hover:bg-red-500/10"><X className="h-4 w-4" /></Button>}
          </div>
          <p className="text-[11px] text-muted-foreground">{hint || "JPG/PNG. File attached directly (no link). Automatically resized & optimised for fast loading."}</p>
        </div>
      </div>
    </div>
  );
}

async function uploadToServer(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data; // { url, name, content_type }
}

function ImageUploader({ value, onChange, testid }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true);
    try { const r = await uploadToServer(file); onChange(r.url); toast.success("Image uploaded"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
    setBusy(false); e.target.value = "";
  };
  return (
    <div className="flex items-center gap-3">
      <div className="h-16 w-16 shrink-0 rounded-lg border border-slate-700 bg-slate-900/60 overflow-hidden flex items-center justify-center">
        {value ? <img src={value} alt="" className="h-full w-full object-cover" /> : <Upload className="h-4 w-4 text-slate-600" />}
      </div>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={pick} data-testid={testid} />
      <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => ref.current?.click()} className="border-slate-700">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}{value ? "Replace" : "Upload"}</Button>
    </div>
  );
}

function PdfUpload({ onUploaded }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true);
    try { const r = await uploadToServer(file); onUploaded({ name: file.name.replace(/\.pdf$/i, ""), url: r.url }); toast.success("PDF uploaded"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
    setBusy(false); e.target.value = "";
  };
  return (
    <>
      <input ref={ref} type="file" accept="application/pdf" className="hidden" onChange={pick} data-testid="documents-pdf-input" />
      <Button type="button" variant="outline" disabled={busy} onClick={() => ref.current?.click()} data-testid="documents-pdf-upload" className="border-slate-700 mt-2">{busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}Upload PDF</Button>
    </>
  );
}

export default function LandingCMS() {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pages, setPages] = useState([]);
  const [pageId, setPageId] = useState(null); // null = Home (main landing)
  const [pageMeta, setPageMeta] = useState(null);

  const loadPages = () => api.get("/landing-pages").then((r) => setPages(r.data.items || [])).catch(() => {});
  const loadContent = (pid) => {
    setC(null);
    if (!pid) { setPageMeta(null); return api.get("/landing").then((r) => setC(r.data)).catch(() => {}); }
    return api.get(`/landing-pages/${pid}`).then((r) => { setC(r.data.content); setPageMeta({ id: r.data.id, name: r.data.name, slug: r.data.slug, status: r.data.status }); }).catch(() => {});
  };
  useEffect(() => { loadPages(); loadContent(null); /* eslint-disable-next-line */ }, []);
  const switchPage = (pid) => { setPageId(pid); loadContent(pid); };

  if (!c) return <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>;

  const set = (section, patch) => setC({ ...c, [section]: { ...c[section], ...patch } });

  const save = async (sections) => {
    setSaving(true);
    try {
      const payload = {};
      sections.forEach((s) => (payload[s] = c[s]));
      if (pageId) await api.patch(`/landing-pages/${pageId}`, { content: payload });
      else await api.patch("/landing", payload);
      toast.success("Landing content saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  const createPage = async () => {
    const name = window.prompt("New landing page name (e.g. Personal Loan Campaign):");
    if (!name) return;
    const slug = window.prompt("Public URL slug:", name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""));
    if (!slug) return;
    try {
      const { data } = await api.post("/landing-pages", { name, slug });
      toast.success("Page created"); await loadPages(); switchPage(data.id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const togglePublish = async () => {
    const next = pageMeta.status === "published" ? "draft" : "published";
    try { await api.patch(`/landing-pages/${pageId}`, { status: next }); setPageMeta({ ...pageMeta, status: next }); loadPages(); toast.success(next === "published" ? "Page published" : "Page unpublished"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deletePage = async () => {
    if (!window.confirm(`Delete "${pageMeta.name}"? This cannot be undone.`)) return;
    try { await api.delete(`/landing-pages/${pageId}`); toast.success("Page deleted"); await loadPages(); switchPage(null); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const h = c.hero, p = c.products, f = c.founder, faq = c.faq, v = c.video, o = c.office, fc = c.final_cta, seo = c.seo, d = c.documents || {};
  const publicUrl = pageMeta ? `${window.location.origin}/p/${pageMeta.slug}` : window.location.origin;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold">Landing Pages</h1>
          <p className="text-muted-foreground text-sm mt-1">Create unlimited landing pages. Home is your main public page (/). No pricing is ever shown publicly.</p>
        </div>
        <a href={publicUrl} target="_blank" rel="noreferrer" className="text-sm text-primary flex items-center gap-1.5" data-testid="view-landing-link"><ExternalLink className="h-4 w-4" />View Page</a>
      </div>

      <div className="glass rounded-xl p-3 flex items-center gap-2 flex-wrap">
        <select value={pageId || ""} onChange={(e) => switchPage(e.target.value || null)} data-testid="page-selector"
          className="bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2 text-sm text-foreground">
          <option value="">🏠 Home (main page)</option>
          {pages.map((pg) => <option key={pg.id} value={pg.id}>{pg.name} {pg.status === "published" ? "• live" : "• draft"}</option>)}
        </select>
        <Button variant="outline" onClick={createPage} data-testid="new-page-button" className="border-slate-700"><Plus className="h-4 w-4 mr-1.5" />New Page</Button>
        {pageMeta && (
          <>
            <span className="text-xs text-muted-foreground ml-1">URL: <span className="font-mono text-slate-300">/p/{pageMeta.slug}</span></span>
            <Button variant="outline" onClick={togglePublish} data-testid="toggle-publish-button" className={pageMeta.status === "published" ? "border-amber-500/40 text-amber-300" : "border-emerald-500/40 text-emerald-300"}>{pageMeta.status === "published" ? "Unpublish" : "Publish"}</Button>
            <Button variant="ghost" onClick={deletePage} data-testid="delete-page-button" className="text-red-400 hover:bg-red-500/10"><Trash2 className="h-4 w-4" /></Button>
          </>
        )}
      </div>

      <Tabs defaultValue="hero">
        <TabsList className="bg-slate-900/60 border border-slate-800 flex-wrap h-auto">
          {["hero", "products", "founder", "faq", "video", "office", "documents", "final", "seo"].map((t) => (
            <TabsTrigger key={t} value={t} data-testid={`cms-tab-${t}`} className="capitalize">{t}</TabsTrigger>
          ))}
        </TabsList>

        {/* HERO */}
        <TabsContent value="hero" className="mt-4">
          <div className="glass rounded-xl p-6 space-y-4">
            <TextRow label="Headline" value={h.headline} onChange={(x) => set("hero", { headline: x })} area />
            <TextRow label="Subheadline" value={h.subheadline} onChange={(x) => set("hero", { subheadline: x })} />
            <TextRow label="Supporting copy" value={h.supporting} onChange={(x) => set("hero", { supporting: x })} area />
            <div className="grid sm:grid-cols-2 gap-3">
              <TextRow label="Form heading" value={h.form_heading} onChange={(x) => set("hero", { form_heading: x })} />
              <TextRow label="CTA text" value={h.cta} onChange={(x) => set("hero", { cta: x })} />
            </div>
            <TextRow label="Form subtext" value={h.form_subtext} onChange={(x) => set("hero", { form_subtext: x })} />
            <div>
              <Label className="text-xs text-muted-foreground">Stats (value | label)</Label>
              {h.stats.map((s, i) => (
                <div key={i} className="grid grid-cols-2 gap-2 mt-2">
                  <Input value={s.value} onChange={(e) => { const st = [...h.stats]; st[i] = { ...st[i], value: e.target.value }; set("hero", { stats: st }); }} className="bg-slate-900/60 border-slate-700" />
                  <Input value={s.label} onChange={(e) => { const st = [...h.stats]; st[i] = { ...st[i], label: e.target.value }; set("hero", { stats: st }); }} className="bg-slate-900/60 border-slate-700" />
                </div>
              ))}
            </div>
            <Button onClick={() => save(["hero"])} disabled={saving} data-testid="save-hero-button" className="bg-primary text-primary-foreground"><Save className="h-4 w-4 mr-1.5" />Save Hero</Button>
          </div>
        </TabsContent>

        {/* PRODUCTS */}
        <TabsContent value="products" className="mt-4">
          <div className="glass rounded-xl p-6 space-y-4">
            <TextRow label="Headline" value={p.headline} onChange={(x) => set("products", { headline: x })} />
            <TextRow label="Description" value={p.description} onChange={(x) => set("products", { description: x })} area />
            <Label className="text-xs text-muted-foreground">Categories</Label>
            <div className="space-y-2">
              {p.categories.map((cat, i) => (
                <div key={i} className="grid grid-cols-[1fr_100px_100px_auto] gap-2 items-center" data-testid={`product-cat-${i}`}>
                  <Input value={cat.name} onChange={(e) => { const a = [...p.categories]; a[i] = { ...a[i], name: e.target.value }; set("products", { categories: a }); }} className="bg-slate-900/60 border-slate-700" placeholder="Name" />
                  <Input value={cat.count} onChange={(e) => { const a = [...p.categories]; a[i] = { ...a[i], count: e.target.value }; set("products", { categories: a }); }} className="bg-slate-900/60 border-slate-700" placeholder="Count" />
                  <Switch checked={cat.active !== false} onCheckedChange={(val) => { const a = [...p.categories]; a[i] = { ...a[i], active: val }; set("products", { categories: a }); }} />
                  <Button size="icon" variant="ghost" onClick={() => set("products", { categories: p.categories.filter((_, idx) => idx !== i) })} className="text-red-400 h-8 w-8"><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
            <Button variant="outline" onClick={() => set("products", { categories: [...p.categories, { name: "New Category", count: "", icon: "layers", active: true, order: p.categories.length + 1 }] })} className="border-slate-700"><Plus className="h-4 w-4 mr-1.5" />Add Category</Button>
            <div><Button onClick={() => save(["products"])} disabled={saving} data-testid="save-products-button" className="bg-primary text-primary-foreground"><Save className="h-4 w-4 mr-1.5" />Save Products</Button></div>
          </div>
        </TabsContent>

        {/* FOUNDER */}
        <TabsContent value="founder" className="mt-4">
          <div className="glass rounded-xl p-6 space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <TextRow label="Name" value={f.name} onChange={(x) => set("founder", { name: x })} />
              <TextRow label="Designation" value={f.designation} onChange={(x) => set("founder", { designation: x })} />
            </div>
            <ImageRow label="Founder Photo" value={f.photo} onChange={(x) => set("founder", { photo: x })} />
            <TextRow label="Vision" value={f.vision} onChange={(x) => set("founder", { vision: x })} area />
            <TextRow label="Quote" value={f.quote} onChange={(x) => set("founder", { quote: x })} area />
            <Button onClick={() => save(["founder"])} disabled={saving} data-testid="save-founder-button" className="bg-primary text-primary-foreground"><Save className="h-4 w-4 mr-1.5" />Save Founder</Button>
          </div>
        </TabsContent>

        {/* FAQ */}
        <TabsContent value="faq" className="mt-4">
          <div className="glass rounded-xl p-6 space-y-3">
            {faq.items.map((item, i) => (
              <div key={i} className="rounded-lg border border-slate-800 p-3 space-y-2" data-testid={`faq-edit-${i}`}>
                <Input value={item.q} onChange={(e) => { const a = [...faq.items]; a[i] = { ...a[i], q: e.target.value }; set("faq", { items: a }); }} className="bg-slate-900/60 border-slate-700" placeholder="Question" />
                <Textarea value={item.a} onChange={(e) => { const a = [...faq.items]; a[i] = { ...a[i], a: e.target.value }; set("faq", { items: a }); }} className="bg-slate-900/60 border-slate-700" placeholder="Answer" />
                <Button size="sm" variant="ghost" onClick={() => set("faq", { items: faq.items.filter((_, idx) => idx !== i) })} className="text-red-400"><Trash2 className="h-4 w-4 mr-1" />Remove</Button>
              </div>
            ))}
            <Button variant="outline" onClick={() => set("faq", { items: [...faq.items, { q: "New question?", a: "Answer" }] })} className="border-slate-700"><Plus className="h-4 w-4 mr-1.5" />Add FAQ</Button>
            <div><Button onClick={() => save(["faq"])} disabled={saving} data-testid="save-faq-button" className="bg-primary text-primary-foreground"><Save className="h-4 w-4 mr-1.5" />Save FAQ</Button></div>
          </div>
        </TabsContent>

        {/* VIDEO */}
        <TabsContent value="video" className="mt-4">
          <div className="glass rounded-xl p-6 space-y-4 max-w-xl">
            <label className="flex items-center justify-between rounded-lg border border-slate-800 p-3"><span className="text-sm">Enable video section</span><Switch checked={!!v.enabled} onCheckedChange={(val) => set("video", { enabled: val })} data-testid="video-enabled" /></label>
            <TextRow label="Title" value={v.title} onChange={(x) => set("video", { title: x })} />
            <TextRow label="Description" value={v.description} onChange={(x) => set("video", { description: x })} area />
            <TextRow label="YouTube URL" value={v.url} onChange={(x) => set("video", { url: x })} />
            <p className="text-[11px] text-muted-foreground">YouTube watch/share/embed link paste karein. Player controls ke saath dikhega aur default me autoplay OFF rahega.</p>
            <Button onClick={() => save(["video"])} disabled={saving} data-testid="save-video-button" className="bg-primary text-primary-foreground"><Save className="h-4 w-4 mr-1.5" />Save Video</Button>
          </div>
        </TabsContent>

        {/* OFFICE */}
        <TabsContent value="office" className="mt-4">
          <div className="glass rounded-xl p-6 space-y-4 max-w-xl">
            <label className="flex items-center justify-between rounded-lg border border-slate-800 p-3"><span className="text-sm">Enable office section</span><Switch checked={!!o.enabled} onCheckedChange={(val) => set("office", { enabled: val })} data-testid="office-enabled" /></label>
            <TextRow label="Heading" value={o.heading} onChange={(x) => set("office", { heading: x })} />
            <TextRow label="Description" value={o.description} onChange={(x) => set("office", { description: x })} area />
            <div>
              <Label className="text-xs text-muted-foreground">Office Images (uploaded directly)</Label>
              <div className="space-y-2 mt-2">
                {(o.images || []).map((img, i) => (
                  <div key={i} className="flex items-center gap-2 rounded-lg border border-slate-800 bg-slate-900/40 p-2">
                    <ImageUploader value={typeof img === "string" ? img : img.url} onChange={(url) => { const a = [...o.images]; a[i] = url; set("office", { images: a }); }} testid={`office-image-input-${i}`} />
                    <Button size="icon" variant="ghost" onClick={() => set("office", { images: o.images.filter((_, idx) => idx !== i) })} className="text-red-400 ml-auto"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                ))}
              </div>
              <Button variant="outline" onClick={() => set("office", { images: [...(o.images || []), ""] })} className="border-slate-700 mt-2" data-testid="office-add-image"><Plus className="h-4 w-4 mr-1.5" />Add Image Slot</Button>
            </div>
            <div><Button onClick={() => save(["office"])} disabled={saving} data-testid="save-office-button" className="bg-primary text-primary-foreground"><Save className="h-4 w-4 mr-1.5" />Save Office</Button></div>
          </div>
        </TabsContent>

        {/* DOCUMENTS */}
        <TabsContent value="documents" className="mt-4">
          <div className="glass rounded-xl p-6 space-y-4 max-w-xl">
            <label className="flex items-center justify-between rounded-lg border border-slate-800 p-3"><span className="text-sm">Enable documents section</span><Switch checked={!!d.enabled} onCheckedChange={(val) => set("documents", { enabled: val })} data-testid="documents-enabled" /></label>
            <TextRow label="Heading" value={d.heading} onChange={(x) => set("documents", { heading: x })} />
            <TextRow label="Description" value={d.description} onChange={(x) => set("documents", { description: x })} area />
            <div>
              <Label className="text-xs text-muted-foreground">PDF Documents (attached directly — previewed inline, no download needed)</Label>
              <div className="space-y-2 mt-2">
                {(d.items || []).map((doc, i) => (
                  <div key={i} className="flex items-center gap-2 rounded-lg border border-slate-800 bg-slate-900/40 p-2">
                    <Input value={doc.name || ""} placeholder="Document name" onChange={(e) => { const a = [...d.items]; a[i] = { ...a[i], name: e.target.value }; set("documents", { items: a }); }} className="bg-slate-900/60 border-slate-700" />
                    {doc.url && <a href={doc.url} target="_blank" rel="noreferrer" className="text-xs text-primary whitespace-nowrap">Preview</a>}
                    <Button size="icon" variant="ghost" onClick={() => set("documents", { items: d.items.filter((_, idx) => idx !== i) })} className="text-red-400"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                ))}
              </div>
              <PdfUpload onUploaded={(doc) => set("documents", { items: [...(d.items || []), doc] })} />
            </div>
            <Button onClick={() => save(["documents"])} disabled={saving} data-testid="save-documents-button" className="bg-primary text-primary-foreground"><Save className="h-4 w-4 mr-1.5" />Save Documents</Button>
          </div>
        </TabsContent>

        {/* FINAL CTA */}
        <TabsContent value="final" className="mt-4">
          <div className="glass rounded-xl p-6 space-y-4">
            <TextRow label="Headline" value={fc.headline} onChange={(x) => set("final_cta", { headline: x })} area />
            <TextRow label="CTA text" value={fc.cta} onChange={(x) => set("final_cta", { cta: x })} />
            <TextRow label="Subtext" value={fc.subtext} onChange={(x) => set("final_cta", { subtext: x })} />
            <Button onClick={() => save(["final_cta"])} disabled={saving} data-testid="save-final-button" className="bg-primary text-primary-foreground"><Save className="h-4 w-4 mr-1.5" />Save Final CTA</Button>
          </div>
        </TabsContent>

        {/* SEO */}
        <TabsContent value="seo" className="mt-4">
          <div className="glass rounded-xl p-6 space-y-4 max-w-xl">
            <TextRow label="Page title" value={seo.title} onChange={(x) => set("seo", { title: x })} />
            <TextRow label="Meta description" value={seo.meta_description} onChange={(x) => set("seo", { meta_description: x })} area />
            <TextRow label="OG title" value={seo.og_title} onChange={(x) => set("seo", { og_title: x })} />
            <TextRow label="OG description" value={seo.og_description} onChange={(x) => set("seo", { og_description: x })} area />
            <TextRow label="OG image URL" value={seo.og_image} onChange={(x) => set("seo", { og_image: x })} />
            <Button onClick={() => save(["seo"])} disabled={saving} data-testid="save-seo-button" className="bg-primary text-primary-foreground"><Save className="h-4 w-4 mr-1.5" />Save SEO</Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
